<template>
  <div class="main-container">
    <div class="main-content-wrap">
      <div class="main-content-pannel">
        <option-menu class="no-bg no-border">
          <div class="content-title-wrap">
            <div class="main-content-title">Projects</div>
            <div class="main-content-menu">
              <div class="searchbox">
                <label for="roleFilter">
                  <i
                    v-if="!filterTxt"
                    class="material-icons-outlined align-middle"
                    >search</i
                  >
                  <i
                    v-else
                    @click="onQuickFilterChanged"
                    class="material-icons-outlined align-middle"
                    >clear</i
                  >
                </label>
                <input
                  id="roleFilter"
                  type="text"
                  @keyup="onQuickFilterChanged"
                  :value="filterTxt"
                  placeholder="Search"
                />
              </div>
              <button
                class="btn primary"
                href="javascript:void(0);"
                @click="addSoftware()"
                :class="{ active: editOpen == 2 }"
                title="Add Project"
              >
                <i class="material-icons-outlined align-middle">add</i>
                Add Software
              </button>
              <button
                class="btn primary"
                href="javascript:void(0);"
                @click="addProject()"
                :class="{ active: editOpen == 1 }"
                title="Add Project"
              >
                <i class="material-icons-outlined align-middle">add</i>
                Add Project
              </button>
            </div>
          </div>
        </option-menu>
        <div class="table-wrapper" style="height: calc(100vh - 201px)">
          <ag-grid-vue
            class="ag-theme-material"
            :gridOptions="projectsGridOptions"
            :columnDefs="projectsColumnDefs"
            @column-resized="onColumnResized"
            @first-data-rendered="onFirstDataRendered"
            @grid-size-changed="onFirstDataRendered"
            :rowData="projectsData"
          ></ag-grid-vue>
          <ag-grid-vue
            class="ag-theme-material"
            :gridOptions="softwareGridOptions"
            :columnDefs="softwareColumnDefs"
            @column-resized="onColumnResized"
            @first-data-rendered="onFirstDataRendered"
            @grid-size-changed="onFirstDataRendered"
            :rowData="softwareData"
          ></ag-grid-vue>
        </div>
      </div>
      <transition name="edit-pannel">
        <edit-pannel v-model="editOpen" @input="closeEditPannel" title>
          <AddProjectPanel
            v-if="editOpen == 1"
            :company="companyId"
            @closePanel="closeEditPannel"
          ></AddProjectPanel>
          <SoftwarePanel
            v-if="editOpen == 2"
            :software="software"
            @buttonClick="onAddSoftware"
            :submitLoading="softwareSubmitLoading"
          />
        </edit-pannel>
      </transition>
    </div>
  </div>
</template>

<script>
import EditPannel from "@/components/EditPannel.vue";
import OptionMenu from "@/components/OptionMenu.vue";
import { AgGridVue } from "ag-grid-vue";
import "ag-grid-enterprise";
import "@/assets/css/ag-grid.scss";
import GridPosition from "./GridPosition.vue";
import AddProjectPanel from "./AddProjectPanel.vue";
import SoftwarePanel from "./SoftwaresPanel.vue";
import { toDbData, randomString, deleteConfirm } from "@/utils/utils";
import * as database from "@/vendor/database";
import GridActions from "./GridActions.vue";

export default {
  props: ["projectsData", "editOpenTitle", "companyId", "company", "software"],
  data() {
    return {
      filterTxt: "",
      projectsGridOptions: {},
      projectsColumnDefs: null,
      editOpen: false,
      softwareSubmitLoading: false,
      softwareGridOptions: {},
      softwareColumnDefs: null,
      softwareData: [],
    };
  },
  components: {
    EditPannel,
    OptionMenu,
    AgGridVue,
    GridPosition,
    AddProjectPanel,
    SoftwarePanel,
    GridActions,
  },
  watch: {
    editOpenTitle: function (val) {
      this.editOpen = val;
    },
    company: function () {
      this.softwareData = this.company?.software ?? [];
    },
  },
  beforeMount() {
    this.createColumnDefs();
  },
  methods: {
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onColumnResized(params) {
      params.api.resetRowHeights();
    },
    onQuickFilterChanged(event) {
      this.filterTxt = event.target.value;
      this.projectsGridOptions.api.setQuickFilter(event.target.value);
      this.softwareGridOptions.api.setQuickFilter(event.target.value);
    },
    createColumnDefs: function () {
      this.projectsGridOptions = {
        defaultColDef: {
          enableRowGroup: true,
          enablePivot: true,
          sortable: true,
          resizable: true,
          filter: true,
          autoHeight: true,
        },
        context: {
          componentParent: this,
        },
        animateRows: true,
        sideBar: false,
        enableCellTextSelection: true,
        pagination: true,
        paginationPageSize: 9,
        paginationNumberFormatter: function (params) {
          return "[" + params.value.toLocaleString() + "]";
        },
      };
      this.projectsColumnDefs = [
        {
          headerName: "Position",
          field: "title",
          cellRendererFramework: "GridPosition",
        },
        {
          headerName: "Type",
          field: "work_type",
        },
        { headerName: "Location", field: "location" },
      ];
      this.softwareGridOptions = {
        defaultColDef: {
          enableRowGroup: true,
          enablePivot: true,
          sortable: true,
          resizable: true,
          filter: true,
          autoHeight: true,
        },
        context: {
          componentParent: this,
        },
        animateRows: true,
        sideBar: false,
        enableCellTextSelection: true,
        pagination: true,
        paginationPageSize: 9,
        paginationNumberFormatter: function (params) {
          return "[" + params.value.toLocaleString() + "]";
        },
      };
      this.softwareColumnDefs = [
        {
          headerName: "Name",
          field: "name",
        },
        {
          headerName: "License",
          field: "license",
        },
        {
          headerName: "Summary",
          field: "summary",
        },
        {
          headerName: "Actions",
          field: "actions",
          cellClass: ["overflow-visible", "grid-action-flex"],
          cellRendererFramework: "GridActions",
          sortable: false,
          filter: false,
          rowGroup: false,
        },
      ];
    },
    setProjectData: function (data) {
      this.$emit("clickItem", data.id);
    },
    addProject: function () {
      this.openEditPannel(1, "Add Project", true);
    },
    addSoftware: function () {
      this.openEditPannel(2, "Add Software", true);
    },
    openEditPannel: function (action, title, isToggle) {
      if (this.editOpen && this.editOpen != action) {
        this.editOpen = action;
        this.emitEditPannel(title);
        return;
      }
      if (isToggle) {
        this.editOpen = this.editOpen ? false : action;
      } else {
        this.editOpen = action;
      }
      this.emitEditPannel(title);
    },
    closeEditPannel: function () {
      this.editOpen = false;
      this.emitEditPannel();
    },
    emitEditPannel: function (title) {
      const param = {
        title: title,
        editOpen: this.editOpen,
      };
      this.$emit("pannelEvent", param);
    },
    onAddSoftware: function (data) {
      if (!this.companyId || !this.company) {
        this.$toasted.global.toast_error({
          message: "current company not found",
        });
        return;
      }
      const dbData = toDbData(data);
      dbData.id = randomString(10);
      let updateData = [];
      if (this.company.software && Array.isArray(this.company.software)) {
        updateData = this.company.software;
      }
      updateData.push(dbData);
      this.softwareSubmitLoading = true;
      database
        .updateDocById("companies", this.companyId, {
          software: updateData,
        })
        .then((result) => {
          if (result.ok) {
            this.closeEditPannel();
            this.$toasted.global.toast_success({
              message: "add success",
            });
          } else {
            this.$toasted.global.toast_error({
              message: "add error",
            });
          }
        })
        .catch((error) => {
          this.$toasted.global.toast_error({
            message: error.toString(),
          });
        })
        .finally(() => {
          this.softwareSubmitLoading = false;
        });
    },
    deleteSoftware: function (data) {
      deleteConfirm(() => {
        if (this.company?.software) {
          const updateData = this.company.software.filter(
            (item) => item.id !== data.id
          );
          database
            .updateDocById("companies", this.companyId, {
              software: updateData,
            })
            .then((result) => {
              if (result.ok) {
                this.$toasted.global.toast_success({
                  message: "delete success",
                });
              } else {
                this.$toasted.global.toast_error({
                  message: "delete error",
                });
              }
            })
            .catch((error) => {
              this.$toasted.global.toast_error({
                message: error.toString(),
              });
            });
        } else {
          this.$toasted.global.toast_error({
            message: "current company not found",
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
}
.table-wrapper > * {
  flex: 1 1 50%;
  max-width: 100%;
}
</style>
